import { Country } from '@appTypes/api.types';
import { RequestStateModel } from '@core/store/store.models';
import {
  CruiseBookKind,
  CruiseBookResult,
  CruiseBookingView,
  CruiseCheck,
  CruiseGuest,
  CruiseHit,
  CruiseLive,
  CruisePackagePick,
  CruiseSailing,
  CruiseSearchOptions,
  CruiseSearchRequest,
  CruiseSearchResult,
  CruiseSort,
  CruiseStep
} from '../cruises.types';
import { todayIso } from '../cruises.utils';

export type CruiseDateMode = 'dates' | 'months';

/**
 * W1's form as the agency set it - the Hotels search form (R-119): dates already parsed from dd.mm.yyyy to ISO, the guests of one cabin as adults +
 * the children's ages on the sailing date (null = not chosen yet, R-115), the lists (empty = any, R-117). Kept in session storage - it holds no personal
 * data (no name, no birth date).
 */
export interface CruiseSearchForm {
  /** always 'dates' since 06.10.2026 (no "Search by months"); kept so an older stored form still parses */
  mode: CruiseDateMode;
  from: string | null;
  to: string | null;
  nightsFrom: number | null;
  nightsTo: number | null;
  adults: number;
  childAges: (number | null)[];
  /** always null since 06.10.2026 (no country selector): every country's ports are listed; kept so an older stored form still parses */
  countryId: string | null;
  portIds: string[];
  shipIds: string[];
  brandIds: string[];
  cabinTypeIds: string[];
  /** R-122: our fare families (empty = any) */
  fareFamilyIds: string[];
  /** R-123: "Cabins available" - on (default) = only the fares on sale now */
  onlyAvailable: boolean;
  /** R-124: our cruising regions (empty = any) */
  regionIds: string[];
  /** R-126: our cruise types (empty = any) */
  cruiseTypeIds: string[];
  /** R-125: "Russian-speaking service" - on = only ships the operator flagged */
  russianSpeakingOnly: boolean;
  priceMin: number | null;
  priceMax: number | null;
  sort: CruiseSort;
}

/** Hotels' defaults: from today, 2 adults, no child, every list "Any". */
export const emptyCruiseSearchForm = (today: string = todayIso()): CruiseSearchForm => ({
  mode: 'dates',
  from: today,
  to: null,
  nightsFrom: null,
  nightsTo: null,
  adults: 2,
  childAges: [],
  countryId: null,
  portIds: [],
  shipIds: [],
  brandIds: [],
  cabinTypeIds: [],
  fareFamilyIds: [],
  onlyAvailable: true,
  regionIds: [],
  cruiseTypeIds: [],
  russianSpeakingOnly: false,
  priceMin: null,
  priceMax: null,
  sort: 'date'
});

const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * A form restored from session storage may be of the older shape (birth-date guests, single ids) - every field is read defensively. A stored country
 * or "months" mode is dropped (06.10.2026): the form no longer shows them, so they must not filter the search invisibly.
 * "Sailing from": today is only the DEFAULT of a new form or a value that cannot be read (absent, not a date string); a date the agent cleared
 * (null / "") stays empty, so Search says "This field is required" under it the Hotels way (R-119, R-130) - it is never snapped back to today.
 */
export const normalizeCruiseSearchForm = (raw: unknown): CruiseSearchForm => {
  const empty = emptyCruiseSearchForm();
  if (!raw || typeof raw !== 'object') return empty;
  const f = raw as Record<string, unknown>;
  const adults = num(f['adults']);
  const cleared = f['from'] === null || f['from'] === '';
  return {
    mode: 'dates',
    from: cleared ? null : (str(f['from']) ?? empty.from),
    to: str(f['to']),
    nightsFrom: num(f['nightsFrom']),
    nightsTo: num(f['nightsTo']),
    adults: adults !== null && adults >= 1 ? adults : empty.adults,
    childAges: Array.isArray(f['childAges']) ? (f['childAges'] as unknown[]).map(num) : [],
    countryId: null,
    portIds: ids(f['portIds']),
    shipIds: ids(f['shipIds']),
    brandIds: ids(f['brandIds']),
    cabinTypeIds: ids(f['cabinTypeIds']),
    fareFamilyIds: ids(f['fareFamilyIds']),
    onlyAvailable: f['onlyAvailable'] !== false,
    regionIds: ids(f['regionIds']),
    cruiseTypeIds: ids(f['cruiseTypeIds']),
    russianSpeakingOnly: f['russianSpeakingOnly'] === true,
    priceMin: num(f['priceMin']),
    priceMax: num(f['priceMax']),
    sort: f['sort'] === 'price' ? 'price' : 'date'
  };
};

/** The guests a search was made for (R-115): the draft's guests and the sailing grid follow them (R-120). */
export interface CruiseSearchedGuests {
  adults: number;
  childAges: number[];
  /** the options' adultAge - the age an adult guest of the draft is sent with (R-116); null = the options were not read */
  adultAge: number | null;
}

/**
 * The booking the agency is putting together (W2-W6). The guests' names and passports live only here, in memory (M-13) - never in
 * session storage. `rfid` once the agency's reservation is registered: a retry books into it, never a second one (R-54).
 */
export interface CruiseWizardDraft {
  channelId: string;
  sailingId: string;
  offerKey: string | null;
  cabinNumber: string | null;
  diningKey: string | null;
  guests: CruiseGuest[];
  packages: CruisePackagePick[];
  rfid: string | null;
  rfNumber: string | null;
  quoteId: string | null;
  rowVersion: string | null;
}

/** The price the agency reads now and what it was asked for (the catalogue cell, then the live answers). */
export interface CruiseShownPrice {
  signature: string;
  total: number;
  currency: string;
}

/** The price-changed dialog (R-3, P4-9): old → new in our price, with the Trace ID of the call that found it. */
export interface CruisePriceChange {
  kind: 'live' | CruiseBookKind;
  oldPrice: number;
  newPrice: number;
  currency: string;
  traceId: string;
  /** kind "live": the answer adopted on Accept */
  pendingLive: CruiseLive | null;
  pendingSignature: string | null;
}

export class CruisesStateModel {
  searchForm: CruiseSearchForm = emptyCruiseSearchForm();
  searchOptions = new RequestStateModel<CruiseSearchOptions>();
  searchResults = new RequestStateModel<CruiseSearchResult>();
  /** the guests the results were searched for - the sailing is opened for them (R-120) */
  searchGuests: CruiseSearchedGuests | null = null;
  /** the request of the last search - a language switch asks it again so the names follow the site language (R-56, website-web#153) */
  searchRequest: CruiseSearchRequest | null = null;
  step: CruiseStep = 'w1';
  hit: CruiseHit | null = null;
  sailing = new RequestStateModel<CruiseSailing>();
  draft: CruiseWizardDraft | null = null;
  shown: CruiseShownPrice | null = null;
  live = new RequestStateModel<CruiseLive>();
  liveSignature: string | null = null;
  priceChange: CruisePriceChange | null = null;
  guestCheck = new RequestStateModel<CruiseCheck>();
  booking = new RequestStateModel<CruiseBookResult>();
  /** the booking the last Quote / Option left (also after a supplier refusal: the cruise kept as a quote, P4-7) */
  booked: CruiseBookingView | null = null;
  countries = new RequestStateModel<Country[]>();
}
